import Card from "../components/Card";
import Button from "../components/Button";
import { Link } from "react-router-dom";

const baseUrl = "https://app.asha-ai.ir";
const docsUrl = "https://asha-ai.ir/docs/docs-group-api-ref/docs-api-ref-overview/";

function CodeBlock({ title, code }: { title: string; code: string }) {
  return (
    <div className="overflow-hidden rounded-btn border border-line">
      <div className="flex items-center justify-between bg-surface-muted px-4 py-2 border-b border-line">
        <span className="text-tiny font-medium text-text-secondary">{title}</span>
        <span className="text-tiny text-text-tertiary">cURL</span>
      </div>
      <pre className="p-4 overflow-x-auto text-small leading-relaxed text-text-main bg-surface" dir="ltr">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Method({ method, path }: { method: string; path: string }) {
  const color =
    method === "POST"
      ? "bg-emerald-100 text-emerald-700"
      : method === "GET"
        ? "bg-blue-100 text-blue-700"
        : "bg-violet-100 text-violet-700";
  return (
    <div className="flex items-center gap-3">
      <span className={`text-tiny font-bold px-2 py-0.5 rounded-full ltr ${color}`} dir="ltr">{method}</span>
      <span className="text-small font-number text-text-main" dir="ltr">{path}</span>
    </div>
  );
}

export default function Docs() {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-h1 text-text-main">مستندات API</h1>
          <p className="text-body text-text-secondary mt-1">راهنمای اتصال به درگاه آشا</p>
        </div>
        <a href={docsUrl} target="_blank" rel="noopener noreferrer">
          <Button size="lg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            مشاهده مستندات کامل
          </Button>
        </a>
      </div>

      {/* Intro */}
      <Card>
        <h2 className="text-h2 text-text-main mb-3">شروع کار</h2>
        <ol className="list-decimal pr-5 space-y-2 text-small text-text-secondary">
          <li>در صفحه «کلیدهای API» یک کلید جدید بسازید.</li>
          <li>در کد خود فقط «آدرس پایه» و «کلید API» را تغییر دهید.</li>
          <li>آدرس پایه: <code className="font-number text-primary" dir="ltr">{baseUrl}/v1</code></li>
          <li>با هر کتابخانه یا SDK سازگار با OpenAI کار کنید.</li>
        </ol>
      </Card>

      {/* Authentication */}
      <Card>
        <h2 className="text-h2 text-text-main mb-1">احراز هویت</h2>
        <p className="text-small text-text-secondary mb-4">
          کلید API را در هدر <code dir="ltr" className="font-number">Authorization: Bearer &lt;KEY&gt;</code> قرار دهید.
        </p>
        <CodeBlock
          title="درخواست نمونه"
          code={`curl ${baseUrl}/v1/models \\
  -H "Authorization: Bearer sk-asha-..."`}
        />
      </Card>

      {/* Chat */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">گفتگو (Chat Completions)</h2>
          <Method method="POST" path="/v1/chat/completions" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "openai/gpt-4o",
    "messages": [
      {"role": "user", "content": "سلام"}
    ]
  }'`}
        />
      </Card>

      {/* Fixed-rate (تعرفه ثابت) — /sub/v1 */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">تعرفه ثابت (Fixed-Rate)</h2>
          <Method method="POST" path="/sub/v1/chat/completions" />
        </div>
        <p className="text-small text-text-secondary mb-4">
          پلن‌های تعرفه ثابت یک سقف مصرف ماهانه دارند و قیمت، مبلغ ثابت ماهانه است
          (بدون کارمزد مصرف‌محور). برای استفاده، همین کلید API خود را با آدرس پایه زیر به
          کار ببرید؛ درخواست‌ها به سرویس تعرفه ثابت هدایت می‌شوند:
          <code className="font-number text-primary" dir="ltr">{baseUrl}/sub/v1</code>
        </p>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/sub/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "claude-fable-5",
    "messages": [
      {"role": "user", "content": "سلام"}
    ]
  }'`}
        />
        <p className="text-small text-text-secondary mt-4 mb-1.5 flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M13 2 17 6 12 10 16 14 11 18 15 22 12 24" />
          </svg>
          <span>
            مدل‌های موجود این پلن را در برگه «تعرفه ثابت» صفحه{" "}
            <Link to="/subscription" className="text-primary font-medium hover:underline">اشتراک‌ها</Link>{" "}
            مشاهده کنید و همان‌جا اشتراک تهیه کنید.
          </span>
        </p>
      </Card>

      {/* Anthropic Messages */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">پیام (Anthropic Messages)</h2>
          <Method method="POST" path="/v1/messages" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/messages \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "anthropic/claude-3-5-sonnet",
    "max_tokens": 1024,
    "messages": [
      {"role": "user", "content": "سلام"}
    ]
  }'`}
        />
      </Card>

      {/* Responses */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">پاسخ (Responses)</h2>
          <Method method="POST" path="/v1/responses" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/responses \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "openai/gpt-4o",
    "input": "سلام، چطوری؟"
  }'`}
        />
      </Card>

      {/* Embeddings */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">جاسازی (Embeddings)</h2>
          <Method method="POST" path="/v1/embeddings" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/embeddings \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "openai/text-embedding-3-small",
    "input": "متن نمونه"
  }'`}
        />
      </Card>

      {/* Images */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">تصویر (Images)</h2>
          <Method method="POST" path="/v1/images/generations" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/images/generations \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "openai/dall-e-3",
    "prompt": "یک گربه فضانورد"
  }'`}
        />
      </Card>

      {/* Video Generation */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h2 text-text-main">تولید ویدیو (Video Generation)</h2>
          <Method method="POST" path="/v1/videos" />
        </div>
        <CodeBlock
          title="مثال"
          code={`curl ${baseUrl}/v1/videos \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-asha-..." \\
  -d '{
    "model": "video-model-slug",
    "prompt": "یک منظره طبیعت"
  }'`}
        />
      </Card>

      {/* Notes */}
      <Card className="bg-primary-light/50">
        <h2 className="text-h2 text-text-main mb-2">نکته‌ها</h2>
        <ul className="space-y-1.5 text-small text-text-secondary">
          <li>• همه مدل‌ها با فرمت OpenAI سازگار هستند.</li>
          <li>• مصرف شما در صفحه «گزارش مصرف» قابل مشاهده است.</li>
          <li>• موجودی کیف پول به صورت خودکار کسر می‌شود.</li>
          <li>• برای پرسش بیشتر، از صفحه «پشتیبانی» تیکت ثبت کنید.</li>
        </ul>
      </Card>
    </div>
  );
}
